import api from './api';
import { ApiResponse, PageResponse } from '../types/api';
import { FornecedorRequestDTO, FornecedorResponseDTO, FornecedorSearchParams } from '../types/supplier';

const baseUrl = '/v1/fornecedores';

export const supplierService = {
  // Listar fornecedores com paginação
  async list(params?: FornecedorSearchParams): Promise<ApiResponse<PageResponse<FornecedorResponseDTO>>> {
    const searchParams = new URLSearchParams();
    
    if (params?.page !== undefined) searchParams.append('page', params.page.toString());
    if (params?.size !== undefined) searchParams.append('size', params.size.toString());
    if (params?.sort) searchParams.append('sort', params.sort);
    if (params?.direction) searchParams.append('direction', params.direction);
    
    // Filtros
    if (params?.filters?.razaoSocial) searchParams.append('razaoSocial', params.filters.razaoSocial);
    if (params?.filters?.cnpj) searchParams.append('cnpj', params.filters.cnpj);
    if (params?.filters?.cidade) searchParams.append('cidade', params.filters.cidade);
    if (params?.filters?.uf) searchParams.append('uf', params.filters.uf);
    if (params?.filters?.ativo !== undefined) searchParams.append('ativo', params.filters.ativo.toString());
    
    const queryString = searchParams.toString();
    const url = queryString ? `${baseUrl}?${queryString}` : baseUrl;
    
    const response = await api.get<ApiResponse<PageResponse<FornecedorResponseDTO>>>(url);
    return response.data;
  },

  // Buscar fornecedor por ID
  async getById(id: string): Promise<ApiResponse<FornecedorResponseDTO>> {
    const response = await api.get<ApiResponse<FornecedorResponseDTO>>(`${baseUrl}/${id}`);
    return response.data;
  },

  // Criar novo fornecedor
  async create(data: FornecedorRequestDTO): Promise<ApiResponse<FornecedorResponseDTO>> {
    const response = await api.post<ApiResponse<FornecedorResponseDTO>>(baseUrl, data);
    return response.data;
  },

  // Atualizar fornecedor
  async update(id: string, data: FornecedorRequestDTO): Promise<ApiResponse<FornecedorResponseDTO>> {
    const response = await api.put<ApiResponse<FornecedorResponseDTO>>(`${baseUrl}/${id}`, data);
    return response.data;
  },

  // Excluir fornecedor
  async delete(id: string): Promise<ApiResponse<void>> {
    const response = await api.delete<ApiResponse<void>>(`${baseUrl}/${id}`);
    return response.data;
  },

  // Ativar fornecedor
  async activate(id: string): Promise<ApiResponse<FornecedorResponseDTO>> {
    const response = await api.patch<ApiResponse<FornecedorResponseDTO>>(`${baseUrl}/${id}/ativar`);
    return response.data;
  },

  // Desativar fornecedor
  async deactivate(id: string): Promise<ApiResponse<FornecedorResponseDTO>> {
    const response = await api.patch<ApiResponse<FornecedorResponseDTO>>(`${baseUrl}/${id}/desativar`);
    return response.data;
  },

  // Buscar fornecedor por CNPJ
  async getByCnpj(cnpj: string): Promise<ApiResponse<FornecedorResponseDTO>> {
    const response = await api.get<ApiResponse<FornecedorResponseDTO>>(`${baseUrl}/cnpj/${cnpj}`);
    return response.data;
  },

  // Validar CNPJ
  async validateCnpj(cnpj: string): Promise<ApiResponse<boolean>> {
    const response = await api.get<ApiResponse<boolean>>(`${baseUrl}/validar-cnpj?cnpj=${cnpj}`);
    return response.data;
  },

  // Pesquisar fornecedores
  async search(query: string, params?: { page?: number; size?: number }): Promise<ApiResponse<PageResponse<FornecedorResponseDTO>>> {
    const searchParams = new URLSearchParams({ q: query });
    if (params?.page !== undefined) searchParams.append('page', params.page.toString());
    if (params?.size !== undefined) searchParams.append('size', params.size.toString());
    
    const response = await api.get<ApiResponse<PageResponse<FornecedorResponseDTO>>>(`${baseUrl}/pesquisar?${searchParams.toString()}`);
    return response.data;
  },

  // Buscar fornecedores por nome
  async getByName(nome: string): Promise<ApiResponse<FornecedorResponseDTO[]>> {
    const response = await api.get<ApiResponse<FornecedorResponseDTO[]>>(`${baseUrl}/nome/${nome}`);
    return response.data;
  },

  // Buscar fornecedores por cidade
  async getByCity(cidade: string): Promise<ApiResponse<PageResponse<FornecedorResponseDTO>>> {
    const response = await api.get<ApiResponse<PageResponse<FornecedorResponseDTO>>>(`${baseUrl}/cidade/${cidade}`);
    return response.data;
  },

  // Buscar fornecedores por estado
  async getByState(estado: string): Promise<ApiResponse<PageResponse<FornecedorResponseDTO>>> {
    const response = await api.get<ApiResponse<PageResponse<FornecedorResponseDTO>>>(`${baseUrl}/estado/${estado}`);
    return response.data;
  },

  // Listar fornecedores ativos
  async getActive(): Promise<ApiResponse<PageResponse<FornecedorResponseDTO>>> {
    const response = await api.get<ApiResponse<PageResponse<FornecedorResponseDTO>>>(`${baseUrl}/ativos`);
    return response.data;
  },

  // Obter fornecedores mais utilizados
  async getMostUsed(): Promise<ApiResponse<FornecedorResponseDTO[]>> {
    const response = await api.get<ApiResponse<FornecedorResponseDTO[]>>(`${baseUrl}/mais-utilizados`);
    return response.data;
  },

  // Obter estatísticas de fornecedores
  async getStatistics(): Promise<ApiResponse<any>> {
    const response = await api.get<ApiResponse<any>>(`${baseUrl}/estatisticas`);
    return response.data;
  },

  // Obter endereço do fornecedor
  async getAddress(id: string): Promise<ApiResponse<any>> {
    const response = await api.get<ApiResponse<any>>(`${baseUrl}/endereco/${id}`);
    return response.data;
  },

  // Obter contatos do fornecedor
  async getContacts(id: string): Promise<ApiResponse<any>> {
    const response = await api.get<ApiResponse<any>>(`${baseUrl}/contatos/${id}`);
    return response.data;
  }
};

export default supplierService;